import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, Bookmark, Highlighter, Search, Zap, X, Gift, Copy, Lightbulb, Smartphone, WifiOff, Check, Download, Cloud } from 'lucide-react';
import { toast } from 'sonner';

const ONBOARDING_KEY = 'lexprocure_onboarding_completed';

const getOnboardingSteps = (referralToken) => [
  {
    title: 'Welcome to LexProcure PH!',
    description: 'Your complete digital reference for Philippine Procurement Laws. Let\'s take a quick tour of the key features.',
    icon: BookOpen,
    color: 'text-emerald-600'
  },
  {
    title: 'AI-Powered Search',
    description: 'Ask questions in natural language and get instant answers from RA 9184, RA 12009, and their IRRs. Our AI understands context and delivers precise results.',
    icon: Sparkles,
    color: 'text-purple-600',
    tip: 'Try: "What are the modes of procurement in RA 12009?"'
  },
  {
    title: 'Mode Selection Wizard',
    description: 'Not sure which procurement mode to use? Our interactive wizard guides you through the decision-making process based on your specific needs.',
    icon: Zap,
    color: 'text-blue-600',
    tip: 'Find it in: ZAP Tools → Mode Selection Wizard'
  },
  {
    title: 'Smart Bookmarks',
    description: 'Save important provisions for quick access later. Your bookmarks sync across devices and are always available offline.',
    icon: Bookmark,
    color: 'text-amber-600',
    tip: 'Click the bookmark icon on any provision page'
  },
  {
    title: 'Highlighting & Notes',
    description: 'Select any text to highlight it and add personal notes. Perfect for study sessions or building your own reference library.',
    icon: Highlighter,
    color: 'text-pink-600',
    tip: 'Just select text on any provision to see options'
  },
  {
    title: 'Advanced Search Filters',
    description: 'Filter search results by reference (RA 9184, RA 12009, IRRs), see content previews, and jump directly to relevant provisions.',
    icon: Search,
    color: 'text-emerald-600',
    tip: 'Use the search bar at the top of any page'
  },
  {
    title: 'Save to Home Screen',
    description: 'Add LexProcure PH to your phone\'s home screen for quick access! It will work like a native app with your device\'s LexProcure PH icon.',
    icon: Smartphone,
    color: 'text-emerald-600',
    tip: 'iOS: Safari → Share → Add to Home Screen | Android: Chrome → Menu → Add to Home Screen',
    homeScreenInfo: true
  },
  {
    title: 'Works Offline',
    description: 'Access previously viewed laws, your bookmarks, highlights, and Zap Tools even without internet connection!',
    icon: WifiOff,
    color: 'text-blue-600',
    tip: 'Content is automatically cached when you view it online',
    offlineInfo: true
  },
  {
    title: 'Share & Earn Rewards',
    description: 'Help your colleagues discover LexProcure PH! Share your unique referral token below. When someone subscribes using your token, you\'ll be eligible for rewards.',
    icon: Gift,
    color: 'text-purple-600',
    referralCode: referralToken,
    tip: 'Find it anytime in: Share&More tab',
    isLastStep: true
  }
];

export default function Onboarding({ isOpen, onClose, referralToken }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingSteps = getOnboardingSteps(referralToken);

  useEffect(() => {
    // Check if user has completed onboarding
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed && isOpen) {
      setShowOnboarding(true);
    }
  }, [isOpen]);

  const handleCopyReferralCode = () => {
    if (referralToken) {
      navigator.clipboard.writeText(referralToken);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
    setCurrentStep(0);
    if (onClose) onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
    setCurrentStep(0);
    if (onClose) onClose();
  };

  const step = onboardingSteps[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / onboardingSteps.length) * 100;

  if (!showOnboarding && !isOpen) return null;

  return (
    <Dialog open={showOnboarding || isOpen} onOpenChange={handleSkip}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200">
          <div 
            className="h-full bg-emerald-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <DialogHeader className="pt-8">
          <div className="flex items-center justify-center mb-4">
            <div className={`p-4 rounded-full bg-gray-100 ${step.color}`}>
              <Icon className="w-12 h-12" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">
            {step.title}
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <p className="text-center text-lg text-gray-700 leading-relaxed">
            {step.description}
          </p>

          {step.referralCode && (
            <div className="space-y-4">
              {/* Token Display */}
              <div className="bg-gradient-to-r from-purple-50 to-emerald-50 border-2 border-purple-300 rounded-lg p-6 space-y-3">
                <div className="text-center">
                  <p className="text-sm font-semibold text-purple-700 mb-2">Your Unique Referral Token</p>
                  <div className="bg-white rounded-lg p-4 border-2 border-purple-200 flex items-center justify-between gap-3">
                    <code className="text-2xl font-bold text-purple-600 tracking-wider flex-1 text-center">
                      {step.referralCode}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyReferralCode}
                      className="border-purple-300 text-purple-600 hover:bg-purple-50"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-center text-purple-700">
                  <span className="font-semibold">Share this token with colleagues!</span> Earn rewards when they subscribe.
                </p>
              </div>

              {/* How It Works Example */}
              <div className="bg-white border-2 border-emerald-300 rounded-lg p-5 space-y-3">
                <p className="text-sm font-bold text-emerald-800 text-center mb-3 flex items-center justify-center gap-2">
                  <Lightbulb className="w-4 h-4" /> How It Works
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-purple-600">1</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-700">
                        <strong className="text-purple-600">Share Your Token:</strong> Give your referral token to colleagues via email, SMS, or the Share button
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-600">2</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-700">
                        <strong className="text-blue-600">They Register:</strong> When someone signs up using your token, the system tracks the referral
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-emerald-600">3</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-700">
                        <strong className="text-emerald-600">They Subscribe:</strong> When they become a paid subscriber, you become eligible for rewards!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-emerald-200">
                  <p className="text-xs text-center text-emerald-700 bg-emerald-50 rounded p-2">
                    <Gift className="w-3 h-3 inline mr-1" />
                    <strong>Example:</strong> You share token <code className="text-purple-600 font-mono">{step.referralCode}</code> → Friend registers → Friend subscribes → You earn rewards!
                  </p>
                </div>
              </div>
            </div>
          )}

          {step.homeScreenInfo && (
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-300 rounded-lg p-6 space-y-4">
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" /> iOS (iPhone/iPad)
                  </p>
                  <ol className="text-xs text-gray-700 space-y-1 ml-4">
                    <li>1. Open this website in <strong>Safari</strong></li>
                    <li>2. Tap the <strong>Share</strong> button (square with arrow)</li>
                    <li>3. Scroll and tap <strong>"Add to Home Screen"</strong></li>
                    <li>4. Tap <strong>"Add"</strong> <Check className="w-3 h-3 inline text-emerald-600" /></li>
                  </ol>
                </div>

                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" /> Android
                  </p>
                  <ol className="text-xs text-gray-700 space-y-1 ml-4">
                    <li>1. Open this website in <strong>Chrome</strong></li>
                    <li>2. Tap the <strong>Menu</strong> (3 dots)</li>
                    <li>3. Tap <strong>"Add to Home screen"</strong> or <strong>"Install App"</strong></li>
                    <li>4. Tap <strong>"Add"</strong> <Check className="w-3 h-3 inline text-emerald-600" /></li>
                  </ol>
                </div>
              </div>

              <p className="text-xs text-center text-emerald-700 bg-emerald-100 rounded p-2 flex items-center justify-center gap-2">
                <Lightbulb className="w-4 h-4" />
                <span><strong>Benefit:</strong> Works like a native app with your LexProcure PH icon, no browser UI, and faster access!</span>
              </p>
            </div>
          )}

          {step.offlineInfo && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-6 space-y-4">
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <WifiOff className="w-5 h-5" /> What Works Offline
                  </p>
                  <div className="space-y-2 text-xs text-gray-700">
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Previously Viewed Laws:</strong> Any law, article, section, or provision you accessed online</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Bookmarks & Highlights:</strong> All your saved items are available offline</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Zap Tools:</strong> Timeline Calculator, Mode Wizard, and all calculators work without internet</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-indigo-200">
                  <p className="text-sm font-bold text-indigo-800 mb-3 flex items-center gap-2">
                    <Cloud className="w-5 h-5" /> How It Works
                  </p>
                  <div className="space-y-2 text-xs text-gray-700">
                    <div className="flex items-start gap-2">
                      <Download className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <span>Content is automatically cached when you view it online</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <WifiOff className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <span>When offline, you'll see a banner and can access cached content</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>Everything syncs automatically when you're back online</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-center text-blue-700 bg-blue-100 rounded p-2 flex items-center justify-center gap-2">
                <Lightbulb className="w-4 h-4" />
                <span><strong>Pro Tip:</strong> Browse content while online to build your offline library!</span>
              </p>
            </div>
          )}

          {step.tip && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-800 flex items-start gap-2">
                <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                <span><span className="font-semibold">Pro Tip:</span> {step.tip}</span>
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Step {currentStep + 1} of {onboardingSteps.length}
          </div>

          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handlePrevious}>
                Previous
              </Button>
            )}
            
            {currentStep < onboardingSteps.length - 1 ? (
              <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
                Next
              </Button>
            ) : (
              <Button onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700">
                Get Started
              </Button>
            )}
          </div>
        </DialogFooter>

        {/* Skip option */}
        <div className="text-center pb-2">
          <button
            onClick={handleSkip}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Skip tutorial
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper function to reset onboarding (can be called from settings)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
};

// Helper function to check if onboarding is completed
export const isOnboardingCompleted = () => {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
};
